import React from 'react';
import axios, { AxiosError } from 'axios';
import ApiHelper, { IUrlParameter } from '../helpers/ApiHelper';
import { useTranslation } from 'react-i18next';
import { useQAlert } from '@kubiq-base/core';
import TranslationNamespaces from '../locales/TranslationNamespaces';

export interface Store {
  getRequest: <T>(
    apiPath: string,
    urlParameters?: IUrlParameter[] | undefined,
    setLoader?: ((isLoading: boolean) => void) | undefined,
    suppressedErrors?: string[] | undefined,
  ) => Promise<T>;
  deleteRequest: <T>(
    apiPath: string,
    urlParameters?: IUrlParameter[] | undefined,
    setLoader?: ((isLoading: boolean) => void) | undefined,
    suppressedErrors?: string[] | undefined,
  ) => Promise<T>;
  postRequest: <T>(
    apiPath: string,
    postData: unknown,
    setLoader?: ((isLoading: boolean) => void) | undefined,
    suppressedErrors?: string[] | undefined,
  ) => Promise<T>;
}

const ApiContext = React.createContext<Store>({
  getRequest: () => new Promise(() => undefined),
  deleteRequest: () => new Promise(() => undefined),
  postRequest: () => new Promise(() => undefined),
});

export const useApi: () => Store = () => React.useContext<Store>(ApiContext);

interface Props {
  children: React.ReactNode;
  accessToken?: string;
}

export const ApiProvider = ({ children, accessToken }: Props): JSX.Element => {
  const { t } = useTranslation(TranslationNamespaces.errors);
  const { enqueueAlert, closeAlert } = useQAlert();

  const getConfig = (): Record<string, unknown> => {
    const config: Record<string, unknown> = {};
    if (accessToken) {
      config.headers = { Authorization: 'bearer ' + accessToken };
    }
    return config;
  };

  const showErrorMessage = (axiosError: AxiosError, suppressedErrors?: string[]): void => {
    const doEnqueueAlert = (textCode: string): void => {
      enqueueAlert(`${t('somethingWentWrong')}`, {
        expanded: {
          message: `${t(textCode)}`,
          action: { label: 'close', onClick: (alertId) => closeAlert(alertId) },
        },
        type: 'error',
      });
    };

    const errorMessage = `^ Request failed with status code ${axiosError.response?.status}. \n Errors:`;
    // eslint-disable-next-line no-console
    console.error(errorMessage, axiosError.response?.data.errors || axiosError.response?.data || 'N/A', axiosError);
    const globalErrors: string[] = axiosError.response?.data.errors?.global;
    if (!globalErrors || globalErrors.length === 0) {
      switch (axiosError.response?.status) {
        case 401:
          !suppressedErrors?.includes('unauthorized') && doEnqueueAlert('unauthorized');
          break;
        default:
          doEnqueueAlert('unknownError');
      }
    } else {
      globalErrors?.forEach((textCode) => {
        if (!suppressedErrors?.includes(textCode)) doEnqueueAlert(textCode);
      });
    }
  };

  const getRequest = <T,>(
    apiPath: string,
    urlParameters?: IUrlParameter[],
    setLoader?: (isLoading: boolean) => void,
    suppressedErrors?: string[],
  ): Promise<T> =>
    new Promise((resolve, reject) => {
      setLoader && setLoader(true);

      let url = ApiHelper.buildUrl(apiPath, urlParameters);
      url = ApiHelper.getFullUrl(url);

      axios
        .get(url, getConfig())
        .then(
          (result) => resolve(result as unknown as T),
          (err: AxiosError) => {
            showErrorMessage(err, suppressedErrors);
            reject(err);
          },
        )
        .finally(() => setLoader && setLoader(false));
    });

  const deleteRequest = <T,>(
    apiPath: string,
    urlParameters?: IUrlParameter[],
    setLoader?: (isLoading: boolean) => void,
    suppressedErrors?: string[],
  ): Promise<T> =>
    new Promise((resolve, reject) => {
      if (setLoader) setLoader(true);

      let url = ApiHelper.buildUrl(apiPath, urlParameters);
      url = ApiHelper.getFullUrl(url);

      axios
        .delete(url, getConfig())
        .then(
          (result) => resolve(result as unknown as T),
          (err: AxiosError) => {
            showErrorMessage(err, suppressedErrors);
            reject(err);
          },
        )
        .finally(() => setLoader && setLoader(false));
    });

  const postRequest = <T,>(
    apiPath: string,
    postData: unknown,
    setLoader?: (isLoading: boolean) => void,
    suppressedErrors?: string[],
  ): Promise<T> =>
    new Promise((resolve, reject) => {
      setLoader && setLoader(true);

      const url = ApiHelper.getFullUrl(apiPath);
      axios
        .post(url, postData, getConfig())
        .then(
          (result) => resolve(result as unknown as T),
          (err: AxiosError) => {
            showErrorMessage(err, suppressedErrors);
            reject(err);
          },
        )
        .finally(() => setLoader && setLoader(false));
    });

  const context = { getRequest, postRequest, deleteRequest };

  return <ApiContext.Provider value={context}>{children}</ApiContext.Provider>;
};
